import React from "react";

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  info: string | null;
}

/**
 * The last boundary before the void.
 *
 * Without one, a throw anywhere under the router unmounts the whole tree and
 * React leaves an empty #root behind: the page is simply the background colour,
 * with nothing in the console, nothing on window.onerror, and no way back
 * except clearing site data. That is what "the screen goes black" was.
 *
 * So this exists as much to make the failure SAYABLE as to make it survivable.
 * It prints the message and stack on the page and re-logs them, because a user
 * hitting this in the wild is the only person who can tell us what broke, and
 * a black rectangle gives them nothing to send.
 *
 * Deliberately dependency-free: no design system imports, no store reads, no
 * hooks. Whatever took the app down might live in any of those, and a fallback
 * that throws while rendering the fallback is worse than no fallback at all.
 * The styles are inline for the same reason.
 */
export class AppCrashBoundary extends React.Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // React swallows this once a boundary handles it, so say it plainly. The
    // component stack is usually the part that identifies the culprit.
    console.error("[flshy] app crashed:", error, info.componentStack);
    this.setState({ info: info.componentStack ?? null });

    // Kept across reloads because the console is not. A crash whose first
    // instinct is to reload takes its own evidence with it, and the component
    // stack is the only part that names what broke.
    try {
      const key = "flshy.crashLog";
      const prev = JSON.parse(localStorage.getItem(key) ?? "[]") as unknown[];
      prev.push({
        at: new Date().toISOString(),
        url: window.location.href,
        message: error.message,
        stack: error.stack ?? null,
        componentStack: info.componentStack ?? null,
      });
      localStorage.setItem(key, JSON.stringify(prev.slice(-5)));
    } catch {
      // Storage full or blocked: the on-screen copy button still works.
    }
  }

  private reload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const detail = [error.stack ?? String(error), info ?? ""]
      .filter(Boolean)
      .join("\n");

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#0a0a0b",
          color: "#f5f5f5",
          fontFamily:
            "ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif",
        }}
      >
        <div style={{ maxWidth: "640px", width: "100%" }}>
          <h1 style={{ fontSize: "20px", fontWeight: 600, margin: "0 0 8px" }}>
            The editor hit an error
          </h1>
          <p
            style={{
              fontSize: "13px",
              lineHeight: 1.6,
              color: "#a1a1aa",
              margin: "0 0 16px",
            }}
          >
            Your project is saved. Reloading picks it up where it left off.
          </p>

          <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
            <button
              type="button"
              onClick={this.reload}
              style={{
                fontSize: "13px",
                fontWeight: 500,
                padding: "8px 16px",
                borderRadius: "8px",
                border: "none",
                background: "#f5f5f5",
                color: "#0a0a0b",
                cursor: "pointer",
              }}
            >
              Reload
            </button>
            <button
              type="button"
              onClick={() => void navigator.clipboard?.writeText(detail)}
              style={{
                fontSize: "13px",
                fontWeight: 500,
                padding: "8px 16px",
                borderRadius: "8px",
                border: "none",
                background: "#26262b",
                color: "#f5f5f5",
                cursor: "pointer",
              }}
            >
              Copy error details
            </button>
          </div>

          <p style={{ fontSize: "13px", color: "#f5f5f5", margin: "0 0 6px" }}>
            {error.message || "Unknown error"}
          </p>
          <pre
            style={{
              fontSize: "12px",
              lineHeight: 1.5,
              color: "#71717a",
              background: "#131316",
              padding: "12px",
              borderRadius: "8px",
              maxHeight: "240px",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              margin: 0,
            }}
          >
            {detail}
          </pre>
        </div>
      </div>
    );
  }
}
